/**
 * `/signup/agency` — self-serve agency signup (Task agency-signup-v1,
 * hardened by Task 293B).
 *
 * Anonymous, Arabic-first, TWO steps on one route.
 *
 *   1. The visitor fills {full_name, agency_name, email} and we POST
 *      `step: 'initiate'`. Nothing is created. The server mails a
 *      one-time link.
 *   2. That link returns here with `?token=…&email=…`. The page collects
 *      the password, POSTs `step: 'verify'`, then signs the user in with
 *      `signInWithPassword` and lands on `/app/onboarding`.
 *
 * The password is deliberately NOT collected on step 1: nothing that
 * could be a secret is held anywhere between the two steps.
 *
 * Existence disclosure: the step-1 response is identical whether or not
 * the address is already registered, so this page has nothing to branch
 * on and must never try. The "you already have an account" answer is
 * shown only on step 2, where the visitor has already proved they own
 * the mailbox by holding the token.
 *
 * Visual shell is the same `<AuthShell>` used by `/login`. Stage machine
 * mirrors `AcceptInvitationPage`'s URL-seeded flow so the UX is
 * consistent.
 *
 * Product copy MUST make it clear this is an AGENCY account (not a
 * client flow) and MUST NOT mention any commercial terms — those land in
 * a follow-up task.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../auth/supabase';
import { signupAgencyInitiate, signupAgencyVerify } from '../api/endpoints';
import { isApiError } from '../api/client';
import { env } from '../env';
import { AuthShell } from '../components/auth/AuthShell';

type Stage =
  | 'form'
  | 'submitting'
  | 'sent'
  | 'verify'
  | 'verifying'
  | 'signing-in'
  | 'recovery'
  | 'invalid'
  | 'success';

const MIN_PASSWORD_LEN = 12;

export function AgencySignupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Read once. The link is server-issued and the address it carries is
  // the one the token was minted for — it is not editable from here.
  const [tokenFromUrl] = useState(() => (params.get('token') ?? '').trim());
  const [emailFromUrl] = useState(() => (params.get('email') ?? '').trim());
  const hasLink = tokenFromUrl.length > 0;
  const linkIsComplete = hasLink && emailFromUrl.length > 0;

  const [fullName, setFullName] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [stage, setStage] = useState<Stage>(
    hasLink ? (linkIsComplete ? 'verify' : 'invalid') : 'form',
  );
  const [error, setError] = useState<string | null>(null);
  const [duplicateEmail, setDuplicateEmail] = useState(false);

  const busy =
    stage === 'submitting' ||
    stage === 'verifying' ||
    stage === 'signing-in' ||
    stage === 'success';

  function describeFailure(err: unknown, fallback: string): string {
    if (isApiError(err)) {
      if (err.code === 'rate_limited') {
        return 'محاولات كثيرة خلال وقت قصير. انتظر قليلاً ثم حاول مجدداً.';
      }
      if (err.code === 'validation_error') {
        return 'تأكد من البيانات المدخلة وحاول مجدداً.';
      }
    }
    return fallback;
  }

  async function onInitiate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setDuplicateEmail(false);

    const trimmedFullName = fullName.trim();
    const trimmedAgencyName = agencyName.trim();
    const trimmedEmail = email.trim();

    if (trimmedFullName.length === 0) {
      setError('أدخل اسمك الكامل.');
      return;
    }
    if (trimmedAgencyName.length === 0) {
      setError('أدخل اسم الوكالة.');
      return;
    }
    if (trimmedEmail.length === 0) {
      setError('أدخل بريدك الإلكتروني.');
      return;
    }
    if (!env.isConfigured) {
      setError('متغيرات البيئة Supabase غير مهيّأة. راجع apps/web/.env.example.');
      return;
    }

    setStage('submitting');
    try {
      await signupAgencyInitiate({
        full_name: trimmedFullName,
        email: trimmedEmail,
        agency_name: trimmedAgencyName,
      });
      // Same outcome for every address, by design. Nothing here reveals
      // whether an account already exists.
      setStage('sent');
    } catch (err) {
      setError(describeFailure(err, 'تعذّر إرسال رسالة التأكيد. حاول مجدداً.'));
      setStage('form');
    }
  }

  async function onVerify(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setDuplicateEmail(false);

    if (password.length < MIN_PASSWORD_LEN) {
      setError(`كلمة المرور يجب أن تكون ${MIN_PASSWORD_LEN} خانة على الأقل.`);
      return;
    }
    if (!env.isConfigured) {
      setError('متغيرات البيئة Supabase غير مهيّأة. راجع apps/web/.env.example.');
      return;
    }

    setStage('verifying');
    try {
      await signupAgencyVerify({
        email: emailFromUrl,
        token: tokenFromUrl,
        password,
      });
      setStage('signing-in');
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: emailFromUrl,
        password,
      });
      if (signInError) {
        // The tenant rows are committed; only the in-flow sign-in
        // failed. Send the user to /login to authenticate normally.
        setStage('recovery');
        return;
      }
      setStage('success');
      navigate('/app/onboarding', { replace: true });
    } catch (err) {
      if (isApiError(err)) {
        if (err.code === 'not_found') {
          setStage('invalid');
          return;
        }
        if (err.code === 'conflict' && err.body?.detail === 'email_already_registered') {
          // Reachable only after the token proved ownership of this
          // mailbox, so naming the reason discloses nothing.
          setDuplicateEmail(true);
          setError(null);
          setStage('verify');
          return;
        }
        if (err.code === 'conflict') {
          setError('هناك محاولة تأكيد جارية لهذا الرابط. انتظر لحظات ثم حاول مجدداً.');
          setStage('verify');
          return;
        }
      }
      setError(describeFailure(err, 'تعذّر إنشاء حساب الوكالة. حاول مجدداً.'));
      setStage('verify');
    }
  }

  if (stage === 'invalid') {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <div className="auth-state-glyph auth-state-glyph--error">
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M15 9l-6 6M9 9l6 6" />
            </svg>
          </div>
          <div className="auth-eyebrow" style={{ textAlign: 'center' }}>
            رابط غير صالح
          </div>
          <h1 className="auth-title" style={{ textAlign: 'center' }}>
            رابط التأكيد <em>غير صالح</em>
          </h1>
          <p className="auth-sub" style={{ textAlign: 'center', marginBottom: 24 }}>
            الرابط ناقص أو منتهي الصلاحية أو تم استخدامه من قبل. ابدأ من جديد وسنرسل لك رابطاً
            جديداً.
          </p>
          <Link to="/signup/agency" className="auth-cta" style={{ textDecoration: 'none' }}>
            <span>البدء من جديد</span>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (stage === 'recovery') {
    return (
      <AuthShell>
        <div className="auth-eyebrow">تم إنشاء حساب الوكالة</div>
        <h1 className="auth-title">
          الحساب <em>جاهز</em>
        </h1>
        <p className="auth-sub">
          أنشأنا وكالتك بنجاح، لكن لم نتمكن من تسجيل دخولك تلقائياً. افتح صفحة تسجيل الدخول واستخدم
          البريد وكلمة المرور اللذين اخترتهما الآن.
        </p>
        <Link to="/login" className="auth-cta" style={{ textDecoration: 'none' }}>
          <span>الانتقال إلى تسجيل الدخول</span>
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </Link>
      </AuthShell>
    );
  }

  if (stage === 'sent') {
    return (
      <AuthShell>
        <div role="status" aria-live="polite">
          <span className="auth-chip">تحقّق من بريدك</span>
          <div className="auth-eyebrow">خطوة واحدة متبقية</div>
          <h1 className="auth-title">
            أرسلنا لك <em>رابط التأكيد</em>
          </h1>
          <p className="auth-sub">
            إذا كان هذا البريد صالحاً للاستخدام فستصلك رسالة خلال دقائق. افتح الرابط من نفس البريد
            لإكمال إنشاء الوكالة واختيار كلمة المرور. الرابط صالح لمرة واحدة وينتهي خلال ٣٠ دقيقة.
          </p>
          <p className="auth-sub">
            لا ننشئ أي حساب قبل فتحك للرابط، ولا نكشف ما إذا كان هذا البريد مسجّلاً لدينا مسبقاً.
          </p>
        </div>
        <button
          type="button"
          className="auth-cta"
          onClick={() => {
            setError(null);
            setStage('form');
          }}
          style={{ marginTop: 8 }}
        >
          <span>لم تصلك الرسالة؟ أعد المحاولة</span>
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
        <p className="tiny" style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 22 }}>
          لديك حساب بالفعل؟{' '}
          <Link to="/login" style={{ fontWeight: 600 }}>
            سجّل دخولك من هنا
          </Link>
          .
        </p>
      </AuthShell>
    );
  }

  if (stage === 'verify' || stage === 'verifying' || stage === 'signing-in') {
    const verifyCta =
      stage === 'verifying'
        ? 'جاري إنشاء حساب الوكالة…'
        : stage === 'signing-in'
          ? 'جاري تسجيل الدخول…'
          : 'تأكيد وإنشاء الوكالة';

    return (
      <AuthShell>
        <span className="auth-chip">تأكيد البريد</span>
        <div className="auth-eyebrow">إكمال إنشاء الوكالة</div>
        <h1 className="auth-title">
          اختر <em>كلمة المرور</em>
        </h1>
        <p className="auth-sub">
          تم التحقق من بريدك عبر الرابط. اختر كلمة مرور لإكمال إنشاء وكالتك — هذه آخر خطوة.
        </p>

        <form onSubmit={onVerify} noValidate aria-busy={busy}>
          {duplicateEmail ? (
            <div className="banner banner-error" role="alert" style={{ marginBottom: 16 }}>
              هذا البريد مسجّل لدينا من قبل. سجّل الدخول من{' '}
              <Link to="/login" style={{ fontWeight: 600 }}>
                صفحة الدخول
              </Link>{' '}
              أو استعد كلمة المرور من{' '}
              <Link to="/forgot-password" style={{ fontWeight: 600 }}>
                صفحة استعادة كلمة المرور
              </Link>
              .
            </div>
          ) : null}
          {error ? (
            <div className="banner banner-error" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </div>
          ) : null}

          <div className="banner banner-info" style={{ marginBottom: 16 }}>
            <strong>البريد:</strong>&nbsp;
            <span dir="ltr">{emailFromUrl}</span>
          </div>

          <div className="auth-field">
            <label className="auth-field-label" htmlFor="signup-password">
              كلمة المرور
            </label>
            <div className="auth-input-wrap">
              <input
                id="signup-password"
                className="auth-input auth-input--has-eye"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                placeholder={`${MIN_PASSWORD_LEN} خانة على الأقل`}
                disabled={stage !== 'verify'}
                autoComplete="new-password"
                dir="ltr"
                required
              />
              <svg
                className="auth-input-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <rect x="4" y="10" width="16" height="11" rx="2" />
                <path d="M8 10V7a4 4 0 1 1 8 0v3" />
              </svg>
              <button
                type="button"
                className="auth-input-eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                aria-pressed={showPassword}
                disabled={stage !== 'verify'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M3 3l18 18" />
                    <path d="M10.6 6.1A11.3 11.3 0 0 1 12 6c6 0 10 6 10 6a14.4 14.4 0 0 1-3 3.6" />
                    <path d="M6.7 6.7A14.5 14.5 0 0 0 2 12s4 6 10 6c1.6 0 3-.4 4.3-1" />
                    <circle cx="12" cy="12" r="2.5" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="auth-cta"
            disabled={stage !== 'verify'}
            style={{ marginTop: 8 }}
          >
            {busy ? <span className="spinner" /> : null}
            <span>{verifyCta}</span>
            {stage === 'verify' ? (
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            ) : null}
          </button>

          <p className="tiny" style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 22 }}>
            الرابط مرتبط بالبريد الصادر من الخادم — لا يمكن تغييره من هنا.
          </p>
        </form>
      </AuthShell>
    );
  }

  const ctaLabel = stage === 'submitting' ? 'جاري إرسال رابط التأكيد…' : 'أرسل رابط التأكيد';

  return (
    <AuthShell>
      <span className="auth-chip">تسجيل وكالة جديدة</span>
      <div className="auth-eyebrow">إنشاء حساب وكالة</div>
      <h1 className="auth-title">
        أنشئ <em>وكالتك</em> الآن
      </h1>
      <p className="auth-sub">
        هذه الصفحة لإنشاء حساب وكالة جديد ستكون أنت مالكها. عملاء الوكالة يُضافون لاحقاً من داخل
        لوحة التحكم — لا تستخدم هذه الصفحة لقبول دعوة من وكالة قائمة.
      </p>
      <p className="auth-sub">
        سنرسل رابط تأكيد إلى بريدك أولاً. لا يتم إنشاء أي حساب قبل فتحك لذلك الرابط.
      </p>

      <form onSubmit={onInitiate} noValidate aria-busy={busy}>
        {!env.isConfigured ? (
          <div className="banner banner-info" style={{ marginBottom: 16 }}>
            متغيرات Supabase غير مهيّأة محلياً. راجع apps/web/.env.example.
          </div>
        ) : null}
        {error ? (
          <div className="banner banner-error" role="alert" style={{ marginBottom: 16 }}>
            {error}
          </div>
        ) : null}

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="signup-full-name">
            الاسم الكامل
          </label>
          <div className="auth-input-wrap">
            <input
              id="signup-full-name"
              className="auth-input"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.currentTarget.value)}
              placeholder="مثال: أحمد القحطاني"
              disabled={stage !== 'form'}
              autoComplete="name"
              required
            />
            <svg
              className="auth-input-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
            </svg>
          </div>
        </div>

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="signup-agency-name">
            اسم الوكالة
          </label>
          <div className="auth-input-wrap">
            <input
              id="signup-agency-name"
              className="auth-input"
              type="text"
              value={agencyName}
              onChange={(e) => setAgencyName(e.currentTarget.value)}
              placeholder="مثال: وكالة الإبداع للتسويق"
              disabled={stage !== 'form'}
              autoComplete="organization"
              required
            />
            <svg
              className="auth-input-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M3 21V8l9-5 9 5v13" />
              <path d="M9 21V12h6v9" />
            </svg>
          </div>
        </div>

        <div className="auth-field">
          <label className="auth-field-label" htmlFor="signup-email">
            البريد الإلكتروني
          </label>
          <div className="auth-input-wrap">
            <input
              id="signup-email"
              className="auth-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="you@super-agency.com"
              disabled={stage !== 'form'}
              autoComplete="email"
              dir="ltr"
              required
            />
            <svg
              className="auth-input-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
          </div>
        </div>

        <button
          type="submit"
          className="auth-cta"
          disabled={stage !== 'form'}
          style={{ marginTop: 8 }}
        >
          {stage === 'submitting' ? <span className="spinner" /> : null}
          <span>{ctaLabel}</span>
          {stage === 'form' ? (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          ) : null}
        </button>

        <p className="tiny" style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 22 }}>
          هل تمت دعوتك للانضمام إلى وكالة قائمة؟{' '}
          <Link to="/login" style={{ fontWeight: 600 }}>
            سجّل دخولك من هنا
          </Link>
          .
        </p>
      </form>
    </AuthShell>
  );
}
